import { Alert, Button, Drawer, Skeleton, Typography } from 'antd'
import {
  convertFfs,
  FFS_DISPLAY_DECIMALS,
  WORKING_UNIT_PRESSURE,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useFfsDisplayUnits } from 'features/indicationTriage/cards/ffsCalc/useFfsDisplayUnits'
import { FormulaDisplay } from 'features/libra/components/FormulaDisplay'
import {
  InputProvenance,
  InputProvenanceRow,
} from 'features/libra/components/InputProvenanceRow'
import { computeMinThicknessValidation } from 'features/libra/stepComponents/ffs/computeMinThicknessValidation'
import { FfsAssetModelEditor } from 'features/libra/stepComponents/ffs/FfsAssetModelEditor'
import { useFfsInputProvenance } from 'features/libra/stepComponents/ffs/useFfsInputProvenance'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import {
  ObservedMinThickness,
  useObservedMinThickness,
} from 'features/libra/stepComponents/ffs/useObservedMinThickness'
import {
  buildMinThicknessFormulaJsx,
  FFS_MIN_THICKNESS_DESCRIPTIONS,
  fmt,
  MinThicknessRawInputs,
} from 'features/libra/stepComponents/FfsMinimumThicknessFormulas'
import { ObservedMinThicknessCheckPanel } from 'features/libra/stepComponents/ObservedMinThicknessCheckPanel'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useActiveScopedFieldAccess } from 'features/libra/ticket/useScopedFieldAccess'
import {
  LibraWorflowStepComponentProps,
  StepValidationResult,
} from 'features/libra/workflow/workflowStepsApi'
import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurementUnit } from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
`

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 1rem;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }
`

const SectionTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.forecast.activeText};
`

/**
 * Why the observed-minimum check can't run, keyed by the non-ready states of
 * `ObservedMinThickness`. A table rather than a chain of `if`s so adding a
 * state is a compile error here instead of a silently missing branch.
 */
const OBSERVED_MIN_BLOCKED_MESSAGE: Record<
  Exclude<ObservedMinThickness['status'], 'ready'>,
  string
> = {
  loading: 'Loading observed minimum thickness check...',
  noCdf:
    'No thickness CDF for this inspection -- observed minimum check cannot run.',
  unreadable:
    'Could not read thickness data for this inspection -- observed minimum check cannot run.',
}

type MinThicknessInputId = 'D' | 'tNom' | 'P' | 'Sy' | 'E' | 'Y' | 'W'

/**
 * The Inputs block, as data: every row is the same `InputProvenanceRow`, so it
 * varies only in symbol, which `raw` field feeds the value, its unit, and the
 * `id` used to look up provenance + description (both keyed by the same id).
 * A row is one line here rather than a hand-written block below.
 */
const INPUT_ROWS: ReadonlyArray<{
  id: MinThicknessInputId
  symbol: ReactNode
  valueKey: keyof MinThicknessRawInputs
  // Canonical measurement kind — drives display-unit conversion. Omitted for
  // dimensionless inputs (E, Y, W).
  measurement?: 'thickness' | 'pressure'
}> = [
  { id: 'D', symbol: <>D</>, valueKey: 'D_mm', measurement: 'thickness' },
  {
    id: 'tNom',
    symbol: (
      <>
        t<sub>nom</sub>
      </>
    ),
    valueKey: 'tNom_mm',
    measurement: 'thickness',
  },
  { id: 'P', symbol: <>P</>, valueKey: 'P_MPa', measurement: 'pressure' },
  {
    id: 'Sy',
    symbol: (
      <>
        S<sub>y</sub>
      </>
    ),
    valueKey: 'Sy_MPa',
    measurement: 'pressure',
  },
  { id: 'E', symbol: <>E</>, valueKey: 'E' },
  { id: 'Y', symbol: <>Y</>, valueKey: 'Y' },
  { id: 'W', symbol: <>W</>, valueKey: 'W_ratio' },
]

/** Canonical working unit for each input-row measurement kind. */
const INPUT_ROW_SOURCE_UNIT = {
  thickness: WORKING_UNIT_THICKNESS,
  pressure: WORKING_UNIT_PRESSURE,
} as const

/**
 * Format one input-row value for display. Dimensionless rows (no `measurement`)
 * render the raw number; dimensioned rows convert the canonical scalar to the
 * preferred display unit and append its name. Precision matches the formula
 * panel (`fmt`).
 */
const formatInputRow = (
  rawNum: number | null,
  measurement: 'thickness' | 'pressure' | undefined,
  displayUnits: {
    thickness: GeckoMeasurementUnit
    pressure: GeckoMeasurementUnit
  }
): string => {
  if (!measurement) return fmt(rawNum)
  const displayUnit = displayUnits[measurement]
  const shown =
    rawNum === null
      ? null
      : convertFfs(rawNum, INPUT_ROW_SOURCE_UNIT[measurement], displayUnit)
  return `${fmt(shown)} ${displayUnit.name}`
}

/**
 * Renders the API 579 piping `t_min` and `MAWP` calculations for the
 * (component, inspection) pair. The step is read-only -- all inputs flow
 * from earlier steps (Asset Model for D / t_nom; Data Gathering for P,
 * S_y, E, Y). Every input row exposes provenance so the engineer
 * can jump back to the cited document or to the Asset Model step. Every
 * formula shows symbolic, substituted, and computed result.
 *
 * `t_sl` (thickness for supplemental loads) is no longer collected or shown
 * as an input row -- it defaults to 0 in `computeMinThicknessValidation`
 * (supplemental loads are assumed not to govern), so the longitudinal
 * formulas below compute with `t_sl = 0` rather than surfacing an editable
 * value.
 *
 * The observed-minimum check at the bottom compares t_min against the
 * minimum reading in the inspection's thickness CDF, reusing the very same
 * cached `useQuantitativeDataSummaryStats` entry the viz panel computes (both
 * resolve the layer via `getCoarsestCdf`). It reports three non-passing
 * states distinctly: still loading, no CDF at all, and CDF present but
 * unreadable.
 */
export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, never>>
> = () => {
  const {
    component,
    componentId,
    inspectionId,
    unit,
    assetModel,
    componentModel,
    isLoading,
    forceRefresh: refreshTicketContext,
  } = useFfsTicketContext()
  // Legacy fallback only: pre-cutover tickets stored the operating params in
  // scoped `fieldValues`. New tickets read everything from the asset-model
  // snapshot (`componentModel`); `computeMinThicknessValidation` prefers the
  // snapshot and falls back to these.
  const { fieldValues } = useActiveScopedFieldAccess()
  // Display units follow the global measurement system. All calculations,
  // comparisons, and the observed-CDS check below stay in the canonical working
  // units (mm / MPa); only what's shown to the engineer is converted.
  const { thickness: thicknessUnit, pressure: pressureUnit } =
    useFfsDisplayUnits()
  // Citations for the asset-model inputs live on the ticket's annotation-only
  // `inputProvenance` overlay. This step DISPLAYS them read-only (keyed by stable
  // field path); editing sources happens in the Asset Model step's editor.
  const { getFieldSource } = useFfsInputProvenance(componentId)

  const observed = useObservedMinThickness({ componentId, inspectionId })
  const observedMinMm =
    observed.status === 'ready' ? observed.observedMinMm : null

  const {
    validation: baseValidation,
    inputs: { raw },
    results,
    missingInputs,
  } = useMemo(
    () =>
      computeMinThicknessValidation({
        componentModel,
        fieldValues,
        observedMinThicknessMm: observedMinMm,
      }),
    [componentModel, fieldValues, observedMinMm]
  )

  const tMinNumForCheck = results.tMin?.toNumber(WORKING_UNIT_THICKNESS) ?? null

  const cdsCheckPass: boolean | null =
    observed.status === 'ready' && tMinNumForCheck !== null
      ? observed.observedMinMm >= tMinNumForCheck
      : null

  // Compose the base (missing-inputs) validation with the observed-CDS check.
  // Error wins over Warning; any non-ready observed state wins over a stale
  // undefined result so the Next button can't activate before the check
  // resolves.
  const finalValidation = useMemo<StepValidationResult>(() => {
    if (baseValidation?.status === 'Error') return baseValidation
    if (tMinNumForCheck !== null && observed.status !== 'ready') {
      return {
        status: 'Warning',
        message: OBSERVED_MIN_BLOCKED_MESSAGE[observed.status],
      }
    }
    if (cdsCheckPass === false) {
      return {
        status: 'Warning',
        message:
          'Observed minimum thickness in the CDS is below the required t_min. Inspect the failing region.',
      }
    }
    return baseValidation
  }, [baseValidation, cdsCheckPass, observed.status, tMinNumForCheck])

  useReportValidation(finalValidation)

  // Asset Model edit drawer -- opened from any InputProvenanceRow with
  // `kind: 'assetModel'`. Engineer fills missing geometry without leaving
  // the step.
  const [isAssetDrawerOpen, setIsAssetDrawerOpen] = useState(false)
  const openAssetDrawer = useCallback(() => setIsAssetDrawerOpen(true), [])
  const closeAssetDrawer = useCallback(() => setIsAssetDrawerOpen(false), [])

  // Single Edit Asset Model drawer shared by BOTH return branches
  // (missing-inputs and main) -- previously duplicated verbatim, a drift
  // trap. Because the editor puts by the passed model's own id/tags, this
  // targets the ticket's snapshot for asset-scoped workflows.
  const assetModelDrawer = (
    <Drawer
      open={isAssetDrawerOpen}
      onClose={closeAssetDrawer}
      placement='right'
      width={720}
      title='Edit Asset Model'
      destroyOnClose>
      {assetModel && component && unit ? (
        <FfsAssetModelEditor
          assetModel={assetModel}
          component={component}
          unit={unit}
          refreshData={refreshTicketContext}
        />
      ) : (
        <Alert
          type='warning'
          showIcon
          message='Asset model not loaded for this ticket.'
          description='The ticket must have a unit with an asset model entry before the editor can open.'
        />
      )}
    </Drawer>
  )

  const provenance = useMemo(() => {
    // Every t_min / MAWP input is an asset-model value. `jsonField` is the
    // stable asset-model JSON key; `label` is the human tooltip. The shared
    // hook resolves the citation (`source`) and the editable `onSourceChange`
    // from the ticket overlay; `onEdit` opens the in-step asset-model drawer.
    const assetModelProv = (
      jsonField: string,
      label: string
    ): InputProvenance => ({
      kind: 'assetModel',
      field: label,
      onEdit: openAssetDrawer,
      source: getFieldSource(jsonField),
    })
    return {
      D: assetModelProv('radius', 'componentModel.radius (*2)'),
      tNom: assetModelProv(
        'wall_thickness',
        'componentModel.wallNominalThickness'
      ),
      P: assetModelProv('design_pressure', 'design_pressure'),
      Sy: assetModelProv('yield_strength', 'yield_strength'),
      E: assetModelProv('joint_efficiency', 'joint_efficiency'),
      Y: assetModelProv('pressure_coefficient', 'pressure_coefficient'),
      W: assetModelProv(
        'weld_strength_reduction',
        'componentModel.weldStrengthReduction'
      ),
    }
  }, [getFieldSource, openAssetDrawer])

  const formulas = useMemo(
    () => buildMinThicknessFormulaJsx(raw, thicknessUnit, pressureUnit),
    [raw, thicknessUnit, pressureUnit]
  )
  const desc = FFS_MIN_THICKNESS_DESCRIPTIONS

  if (isLoading) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    )
  }

  if (missingInputs.length > 0) {
    const hasAssetModelMissing = missingInputs.some((m) =>
      m.toLowerCase().includes('asset model')
    )
    const canOpenAssetDrawer = Boolean(assetModel && component && unit)
    return (
      <Container>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          Minimum Thickness Analysis
        </Typography.Title>
        <Alert
          type='error'
          showIcon
          message='Missing inputs from earlier steps.'
          description={
            <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
              {missingInputs.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          }
          action={
            hasAssetModelMissing && canOpenAssetDrawer ? (
              <Button size='small' type='primary' onClick={openAssetDrawer}>
                Edit Asset Model
              </Button>
            ) : null
          }
        />
        {assetModelDrawer}
      </Container>
    )
  }

  // Displayed results follow the toggle. (tMinNumForCheck above stays canonical
  // mm for the observed-CDS comparison.)
  const tMinCNum = results.tMinC?.toNumber(thicknessUnit) ?? null
  const tMinLNum = results.tMinL?.toNumber(thicknessUnit) ?? null
  const tMinNum = results.tMin?.toNumber(thicknessUnit) ?? null
  const mawpRCNum = results.mawpRC?.toNumber(pressureUnit) ?? null
  const mawpRLNum = results.mawpRL?.toNumber(pressureUnit) ?? null
  const mawpRNum = results.mawpR?.toNumber(pressureUnit) ?? null
  const thicknessUnitName = thicknessUnit.name
  const pressureUnitName = pressureUnit.name

  return (
    <Container>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        Minimum Thickness Analysis
      </Typography.Title>
      <Typography.Paragraph type='secondary' style={{ marginBottom: 0 }}>
        Component-level API 579 piping calculations: compute the minimum
        required thickness (t<sub>min</sub>) for the design pressure, then check
        it against the smallest measured remaining thickness. When that check
        fails, this step also reports the re-rated maximum allowable working
        pressure (MAWP_r) the current corroded wall can sustain.
      </Typography.Paragraph>

      <SectionBlock>
        <SectionTitle>Inputs</SectionTitle>
        {INPUT_ROWS.map(({ id, symbol, valueKey, measurement }) => (
          <InputProvenanceRow
            key={id}
            symbol={symbol}
            value={formatInputRow(raw[valueKey], measurement, {
              thickness: thicknessUnit,
              pressure: pressureUnit,
            })}
            provenance={provenance[id]}
            description={desc[id]}
          />
        ))}
      </SectionBlock>

      <SectionBlock>
        <SectionTitle>
          Minimum Thickness (t<sub>min</sub>)
        </SectionTitle>
        {tMinCNum !== null ? (
          <FormulaDisplay
            label='Circumferential -- API 579 Annex 2C.5.3.1'
            description={desc.tMinC}
            symbolic={formulas.tMinC.symbolic}
            substituted={formulas.tMinC.substituted}
            result={{
              value: tMinCNum,
              unit: thicknessUnitName,
              precision: FFS_DISPLAY_DECIMALS,
            }}
          />
        ) : null}
        {tMinLNum !== null ? (
          <FormulaDisplay
            label='Longitudinal -- API 579 Annex 2C.5.3.2'
            description={desc.tMinL}
            symbolic={formulas.tMinL.symbolic}
            substituted={formulas.tMinL.substituted}
            result={{
              value: tMinLNum,
              unit: thicknessUnitName,
              precision: FFS_DISPLAY_DECIMALS,
            }}
          />
        ) : null}
        {tMinNum !== null ? (
          <FormulaDisplay
            label={
              <>
                Governing t<sub>min</sub> = max(t<sub>min</sub>
                <sup>C</sup>, t<sub>min</sub>
                <sup>L</sup>)
              </>
            }
            description={desc.tMin}
            symbolic={
              <>
                t<sub>min</sub> = max(t<sub>min</sub>
                <sup>C</sup>, t<sub>min</sub>
                <sup>L</sup>)
              </>
            }
            substituted={
              <>
                max({fmt(tMinCNum)}, {fmt(tMinLNum)})
              </>
            }
            result={{
              value: tMinNum,
              unit: thicknessUnitName,
              precision: FFS_DISPLAY_DECIMALS,
            }}
          />
        ) : null}
      </SectionBlock>

      <SectionBlock>
        <SectionTitle>Observed Minimum Thickness Check</SectionTitle>
        <ObservedMinThicknessCheckPanel
          tMinMm={tMinNumForCheck}
          observed={observed}
          pass={cdsCheckPass}
          requiredDescription={desc.requiredTMin}
          observedDescription={desc.observedMin}
        />
      </SectionBlock>

      {cdsCheckPass === false && (
        <SectionBlock>
          <SectionTitle>
            Re-rated MAWP (MAWP<sub>r</sub>) -- at Measured Minimum Thickness
          </SectionTitle>
          {mawpRCNum !== null ? (
            <FormulaDisplay
              label='Circumferential -- API 579 Annex 2C.5.3.1'
              description={desc.mawpRC}
              symbolic={formulas.mawpRC.symbolic}
              substituted={formulas.mawpRC.substituted}
              result={{
                value: mawpRCNum,
                unit: pressureUnitName,
                precision: FFS_DISPLAY_DECIMALS,
              }}
            />
          ) : null}
          {mawpRLNum !== null ? (
            <FormulaDisplay
              label='Longitudinal -- API 579 Annex 2C.5.3.2'
              description={desc.mawpRL}
              symbolic={formulas.mawpRL.symbolic}
              substituted={formulas.mawpRL.substituted}
              result={{
                value: mawpRLNum,
                unit: pressureUnitName,
                precision: FFS_DISPLAY_DECIMALS,
              }}
            />
          ) : null}
          {mawpRNum !== null ? (
            <FormulaDisplay
              label={
                <>
                  Governing MAWP<sub>r</sub> = min(MAWP<sub>r</sub>
                  <sup>C</sup>, MAWP<sub>r</sub>
                  <sup>L</sup>)
                </>
              }
              description={desc.mawpR}
              symbolic={
                <>
                  MAWP<sub>r</sub> = min(MAWP<sub>r</sub>
                  <sup>C</sup>, MAWP<sub>r</sub>
                  <sup>L</sup>)
                </>
              }
              substituted={
                <>
                  min({fmt(mawpRCNum)}, {fmt(mawpRLNum)})
                </>
              }
              result={{
                value: mawpRNum,
                unit: pressureUnitName,
                precision: FFS_DISPLAY_DECIMALS,
              }}
            />
          ) : null}
        </SectionBlock>
      )}

      {assetModelDrawer}
    </Container>
  )
}
